import { useEffect, useRef, useState, useCallback } from "react";
import { Send, ArrowLeft, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

export default function ChatDialog({ open, onClose, initialListingId }) {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  const loadThreads = useCallback(async () => {
    const { data } = await api.get("/threads");
    setThreads(data);
    return data;
  }, []);

  const openThread = useCallback(async (t) => {
    const { data } = await api.get(`/threads/${t.thread_id}`);
    setActive(data);
    loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    if (!open) { setActive(null); setText(""); return; }
    (async () => {
      await loadThreads();
      if (initialListingId) {
        try {
          const { data } = await api.post("/threads", { listing_id: initialListingId });
          setActive(data);
          loadThreads();
        } catch (e) { console.debug("could not open thread (own listing?)", e?.response?.status); }
      }
    })();
  }, [open, initialListingId, loadThreads]);

  // poll active thread (intentionally keyed on thread id only, not the whole `active` object)
  useEffect(() => {
    if (!open || !active) return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`/threads/${active.thread_id}`);
        setActive(data);
      } catch (e) { console.error("thread poll failed", e); }
    }, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, active?.thread_id]);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [active?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    const { data } = await api.post(`/threads/${active.thread_id}/messages`, { text });
    setActive((a) => ({ ...a, messages: [...(a.messages || []), data] }));
    setText("");
    loadThreads();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-lg p-0 overflow-hidden" data-testid="chat-dialog">
        <DialogHeader className="px-5 pt-5">
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            {active && <button onClick={() => setActive(null)} data-testid="chat-back-btn" className="h-7 w-7 rounded-full hover:bg-slate-100 flex items-center justify-center"><ArrowLeft className="h-4 w-4" /></button>}
            {active ? active.other_name : "Messages"}
          </DialogTitle>
        </DialogHeader>

        {!active ? (
          <div className="px-5 pb-5 max-h-[60vh] overflow-y-auto" data-testid="thread-list">
            {threads.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">No conversations yet. Open an item and tap "Message seller".</p>}
            {threads.map((t) => (
              <button key={t.thread_id} data-testid={`thread-${t.thread_id}`} onClick={() => openThread(t)}
                className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-slate-50 text-left">
                {t.listing_image ? <img src={t.listing_image} alt="" className="h-11 w-11 rounded-xl object-cover" /> : <span className="h-11 w-11 rounded-xl bg-slate-100 flex items-center justify-center"><MessageCircle className="h-5 w-5 text-slate-400" /></span>}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-800 text-sm">{t.other_name}</div>
                  <div className="text-xs text-slate-400 truncate">{t.listing_title} · {t.last_text || "Say hi!"}</div>
                </div>
                {t.unread > 0 && <span data-testid={`thread-unread-${t.thread_id}`} className="min-w-5 h-5 px-1 rounded-full bg-[#FF5A79] text-white text-xs font-bold flex items-center justify-center">{t.unread}</span>}
              </button>
            ))}
          </div>
        ) : (
          <div className="flex flex-col h-[60vh]">
            <div className="px-5 py-2 bg-slate-50 text-xs text-slate-500 flex items-center gap-2">
              {active.listing_image && <img src={active.listing_image} alt="" className="h-8 w-8 rounded-lg object-cover" />}
              About: <span className="font-semibold text-slate-700">{active.listing_title}</span>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-2" data-testid="chat-messages">
              {(active.messages || []).map((m) => {
                const mine = m.sender_id === user?.user_id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-[#FF5A79] text-white" : "bg-slate-100 text-slate-700"}`}>
                      {m.text}
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={send} className="p-4 flex gap-2 border-t border-slate-100">
              <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="rounded-full h-11" />
              <Button type="submit" size="icon" data-testid="chat-send-btn" className="rounded-full h-11 w-11 bg-[#FF5A79] hover:bg-[#e04865] shrink-0"><Send className="h-4 w-4" /></Button>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
